import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

const API_URL = 'https://iia-backend-production.up.railway.app';

const EnrollmentAgreement = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fullName, setFullName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAgreement();
  }, []);

  const fetchAgreement = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admissions/agreement`);
      setAgreement(res.data.agreement);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async () => {
    if (!fullName.trim()) {
      setError('Please type your full name to sign.');
      return;
    }
    if (!agreed) {
      setError('Please check the agreement box.');
      return;
    }
    if (fullName.trim().toLowerCase() !== 
        `${user.first_name} ${user.last_name}`.toLowerCase()) {
      setError(`Please type your full name exactly: ${user.first_name} ${user.last_name}`);
      return;
    }

    setSigning(true);
    try {
      await axios.post(`${API_URL}/api/admissions/agreement/sign`);
      navigate('/campus');
    } catch (err) {
      setError('Error signing agreement. Please try again.');
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <p style={styles.loadingText}>Loading agreement...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.logo}>IIA</h1>
          <p style={styles.logoSub}>Impact Influence Academy</p>
          <h2 style={styles.title}>Enrollment Agreement</h2>
          <p style={styles.subtitle}>
            Please read the following agreement carefully before signing.
          </p>
        </div>

        {/* Agreement Text */}
        {agreement ? (
          <>
            <div style={styles.agreementBox}>
              <div style={styles.agreementHeader}>
                <h3 style={styles.agreementTitle}>{agreement.title}</h3>
                <span style={styles.versionBadge}>Version {agreement.version}</span>
              </div>
              <div style={styles.agreementBody}>
                {agreement.body.split('\n').map((line, i) => (
                  <p key={i} style={line.trim() === '' ? styles.spacer : styles.agreementLine}>
                    {line}
                  </p>
                ))}
              </div>
            </div>

            {/* Signature Section */}
            <div style={styles.signatureSection}>
              <h3 style={styles.signatureTitle}>Sign This Agreement</h3>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Type your full name to sign
                </label>
                <input
                  style={styles.input}
                  placeholder={`${user?.first_name} ${user?.last_name}`}
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                />
                <p style={styles.nameHint}>
                  Must match exactly: {user?.first_name} {user?.last_name}
                </p>
              </div>

              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={e => setAgreed(e.target.checked)}
                  style={styles.checkbox}
                />
                <span style={styles.checkboxText}>
                  I have read, understood, and agree to the terms of this 
                  Enrollment Agreement. I understand this is a legally binding 
                  document.
                </span>
              </label>

              {error && <div style={styles.errorBox}>{error}</div>}

              <div style={styles.signatureInfo}>
                <p style={styles.signatureInfoText}>
                  📅 Signing as: <strong>{user?.first_name} {user?.last_name}</strong>
                </p>
                <p style={styles.signatureInfoText}>
                  🕐 Date: <strong>{new Date().toLocaleDateString('en-US', {
                    weekday: 'long', year: 'numeric', 
                    month: 'long', day: 'numeric'
                  })}</strong>
                </p>
              </div>

              <button
                style={signing || !agreed || !fullName ? 
                  styles.signBtnDisabled : styles.signBtn}
                onClick={handleSign}
                disabled={signing || !agreed || !fullName}
              >
                {signing ? 'Signing...' : '✍️ Sign & Enter Campus'}
              </button>
            </div>
          </>
        ) : (
          <div style={styles.noAgreement}>
            <p style={styles.noAgreementText}>
              No enrollment agreement has been posted yet. 
              Please contact your administrator.
            </p>
            <button style={styles.signBtn} onClick={() => navigate('/campus')}>
              Continue to Campus
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1A2744',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '40px 20px',
  },
  card: {
    backgroundColor: '#0f1a33',
    border: '1px solid #ffffff15',
    borderRadius: '16px',
    padding: '48px',
    width: '100%',
    maxWidth: '720px',
  },
  header: { textAlign: 'center', marginBottom: '32px' },
  logo: { color: '#C49A28', fontSize: '32px', fontWeight: '800', margin: '0', letterSpacing: '3px' },
  logoSub: { color: '#ffffff40', fontSize: '12px', letterSpacing: '2px', margin: '4px 0 24px 0', textTransform: 'uppercase' },
  title: { color: '#ffffff', fontSize: '22px', fontWeight: '700', margin: '0 0 8px 0' },
  subtitle: { color: '#ffffff60', fontSize: '14px', margin: '0' },
  agreementBox: {
    backgroundColor: '#1A2744',
    border: '1px solid #ffffff15',
    borderRadius: '10px',
    marginBottom: '32px',
    overflow: 'hidden',
  },
  agreementHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid #ffffff10',
    backgroundColor: '#0f1a33',
  },
  agreementTitle: { color: '#C49A28', fontSize: '16px', fontWeight: '700', margin: '0' },
  versionBadge: { color: '#ffffff40', fontSize: '12px', backgroundColor: '#ffffff10', padding: '3px 10px', borderRadius: '4px' },
  agreementBody: {
    padding: '24px',
    maxHeight: '400px',
    overflowY: 'auto',
  },
  agreementLine: { color: '#ffffffcc', fontSize: '14px', lineHeight: '1.8', margin: '0 0 4px 0' },
  spacer: { margin: '8px 0', height: '4px' },
  signatureSection: {
    backgroundColor: '#1A2744',
    border: '1px solid #C49A2830',
    borderRadius: '10px',
    padding: '28px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  signatureTitle: { color: '#C49A28', fontSize: '16px', fontWeight: '700', margin: '0' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { color: '#ffffff80', fontSize: '13px', fontWeight: '600' },
  input: {
    backgroundColor: '#0f1a33',
    border: '1px solid #ffffff20',
    borderRadius: '8px',
    padding: '12px 14px',
    color: '#ffffff',
    fontSize: '15px',
    outline: 'none',
    boxSizing: 'border-box',
    width: '100%',
    fontStyle: 'italic',
  },
  nameHint: { color: '#ffffff40', fontSize: '12px', margin: '0' },
  checkboxLabel: { display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' },
  checkbox: { marginTop: '2px', flexShrink: 0, width: '16px', height: '16px', cursor: 'pointer' },
  checkboxText: { color: '#ffffffcc', fontSize: '14px', lineHeight: '1.6' },
  errorBox: { backgroundColor: '#ff000015', border: '1px solid #ff000040', borderRadius: '8px', padding: '12px 16px', color: '#ff6666', fontSize: '13px' },
  signatureInfo: { backgroundColor: '#0f1a33', borderRadius: '8px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '6px' },
  signatureInfoText: { color: '#ffffff80', fontSize: '13px', margin: '0' },
  signBtn: { backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '8px', padding: '14px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', width: '100%' },
  signBtnDisabled: { backgroundColor: '#C49A2840', color: '#1A274460', border: 'none', borderRadius: '8px', padding: '14px', fontSize: '15px', fontWeight: '700', cursor: 'not-allowed', width: '100%' },
  noAgreement: { textAlign: 'center', padding: '40px', display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' },
  noAgreementText: { color: '#ffffff60', fontSize: '14px' },
  loadingText: { color: '#ffffff60', fontSize: '14px', textAlign: 'center', marginTop: '100px' },
};

export default EnrollmentAgreement;